import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import classnames from "classnames";
import panzoom from "panzoom";
import { useResizeDetector } from "react-resize-detector";
import { usePdfWorkerSrc } from "../services/hooks/index.js";
import { CircularProgress } from "../CircularProgress/index.js";
import { Box } from "../Box/index.js";
import { Dialog } from "../Dialog/index.js";
import { ImageWithLoader } from "../ImageWithLoader/index.js";
import { usePanZoomBoundaries } from "./hooks/index.js";
import { PreviewButtons } from "./PreviewButtons/index.js";
import { useStyles } from "./FilePreviewWithZoom.styles.js";
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) resolve(value);
    else Promise.resolve(value).then(_next, _throw);
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(void 0);
        });
    };
}
const options = {
    isEvalSupported: false
};
const FilePreviewWithZoom = ({ open, onClose, onLoadSuccess, onLoadFull, url, withBottomOffset, fileType = 'pdf', alternativeImageComponent = false })=>{
    const panZoomRef = useRef(null);
    const paperRef = useRef(null);
    const [dialogContainer, setDialogContainer] = useState(null);
    const [objectUrl, setObjectUrl] = useState(url);
    const setPaperRef = useCallback((node)=>{
        var _ref;
        paperRef.current = node;
        setDialogContainer(null != (_ref = null == node ? void 0 : node.parentElement) ? _ref : null);
    }, []);
    const pdfWorkerSrc = usePdfWorkerSrc();
    if (pdfjs.GlobalWorkerOptions.workerSrc !== pdfWorkerSrc) pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
    const { classes } = useStyles();
    const zoomLevels = [
        0.25,
        0.5,
        0.75,
        1,
        1.25,
        1.5,
        1.75,
        2,
        3,
        4,
        5
    ];
    const [currentZoom, setCurrentZoom] = useState(1);
    const [pagesCount, setPagesCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const { checkBoundaries, setupBoundaryCheck } = usePanZoomBoundaries(panZoomRef, paperRef);
    const { width } = useResizeDetector({
        targetRef: {
            current: dialogContainer
        },
        refreshMode: 'debounce',
        refreshRate: 100
    });
    useEffect(()=>{
        let isCancelled = false;
        let nextObjectUrl = '';
        const loadFile = ()=>_async_to_generator(function*() {
                try {
                    if (onLoadFull) {
                        const file = yield onLoadFull();
                        if (!isCancelled) {
                            nextObjectUrl = URL.createObjectURL(file);
                            setObjectUrl(nextObjectUrl);
                        }
                    }
                } catch (e) {}
            })();
        loadFile();
        return ()=>{
            isCancelled = true;
            if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
        };
    }, [
        onLoadFull
    ]);
    const reset = ()=>{
        if (panZoomRef.current) {
            setCurrentZoom(1);
            setPageNumber(1);
            panZoomRef.current.dispose();
            panZoomRef.current = null;
        }
    };
    useEffect(()=>{
        if (!open) reset();
    }, [
        open
    ]);
    useEffect(()=>()=>{
            reset();
        }, [
        url
    ]);
    const zoomTo = (scale)=>{
        if (!paperRef.current || !panZoomRef.current) return;
        const { offsetWidth, offsetHeight } = paperRef.current;
        const centerX = offsetWidth / 2;
        const centerY = offsetHeight / 2;
        panZoomRef.current.smoothZoomAbs(centerX, centerY, scale);
    };
    const handleLoadSuccess = useCallback(({ numPages })=>{
        setPagesCount(numPages || 1);
        requestAnimationFrame(()=>{
            if (!open || !paperRef.current) return;
            panZoomRef.current = panzoom(paperRef.current, {
                maxZoom: 5,
                minZoom: 0.25,
                transformOrigin: {
                    x: 0,
                    y: 0
                },
                initialZoom: 1,
                beforeWheel: ()=>true,
                beforeMouseDown: (event)=>{
                    var _paperRef_current;
                    return !(null == (_paperRef_current = paperRef.current) ? void 0 : _paperRef_current.contains(event.target));
                },
                smoothScroll: false,
                zoomDoubleClickSpeed: 1
            });
            null == onLoadSuccess || onLoadSuccess();
        });
    }, [
        open,
        onLoadSuccess,
        setPagesCount
    ]);
    setupBoundaryCheck();
    const handleZoom = (scale_0)=>{
        zoomTo(scale_0);
        setTimeout(()=>{
            checkBoundaries();
        }, 300);
        setCurrentZoom(scale_0);
    };
    if (!open) return null;
    const renderDocument = (pageWidth)=>{
        if (!pageWidth) return /*#__PURE__*/ jsx(Box, {
            display: "flex",
            justifyContent: "center",
            p: "32",
            children: /*#__PURE__*/ jsx(CircularProgress, {
                size: 50
            })
        });
        return /*#__PURE__*/ jsx(Document, {
            options: options,
            file: url,
            className: classnames(classes.document),
            error: /*#__PURE__*/ jsx(Box, {
                p: "32",
                color: "text.light.secondary",
                textAlign: "center",
                children: "Ошибка при загрузке файла"
            }),
            loading: /*#__PURE__*/ jsx(Box, {
                display: "flex",
                justifyContent: "center",
                p: "32",
                children: /*#__PURE__*/ jsx(CircularProgress, {
                    size: 50
                })
            }),
            onLoadSuccess: handleLoadSuccess,
            children: /*#__PURE__*/ jsx(Page, {
                width: pageWidth,
                scale: Math.max(1.5, currentZoom),
                pageNumber: pageNumber,
                renderAnnotationLayer: true,
                loading: /*#__PURE__*/ jsx(Box, {
                    display: "flex",
                    justifyContent: "center",
                    p: "32",
                    children: /*#__PURE__*/ jsx(CircularProgress, {
                        size: 50
                    })
                })
            })
        });
    };
    const renderImage = ()=>/*#__PURE__*/ jsxs(Box, {
            children: [
                /*#__PURE__*/ jsx("div", {
                    className: classes.imageWrapper
                }),
                /*#__PURE__*/ jsx("img", {
                    className: classnames(classes.image),
                    src: objectUrl,
                    alt: "",
                    onLoad: ()=>handleLoadSuccess({})
                })
            ]
        });
    const renderImageWithLoader = ()=>/*#__PURE__*/ jsx(ImageWithLoader, {
            objectFit: "contain",
            src: objectUrl,
            height: "100%",
            width: "100%",
            onLoad: ()=>handleLoadSuccess({})
        });
    return /*#__PURE__*/ jsxs(Box, {
        children: [
            /*#__PURE__*/ jsx(Dialog, {
                scroll: "body",
                open: open,
                onClose: onClose,
                classes: {
                    paper: classes.dialogPaper
                },
                slotProps: {
                    paper: {
                        ref: setPaperRef
                    }
                },
                children: /*#__PURE__*/ jsxs(Box, {
                    children: [
                        'pdf' === fileType && renderDocument(width),
                        'image' === fileType && !alternativeImageComponent && renderImage(),
                        'image' === fileType && alternativeImageComponent && renderImageWithLoader()
                    ]
                })
            }),
            /*#__PURE__*/ jsx(PreviewButtons, {
                handleNextPage: setPageNumber,
                pagesCount: pagesCount,
                nextPageNumber: pageNumber,
                pageNumber: pageNumber,
                scale: currentZoom,
                zoomLevels: zoomLevels,
                handleZoom: handleZoom,
                onClose: onClose,
                withBottomOffset: withBottomOffset
            })
        ]
    });
};
export { FilePreviewWithZoom };
